import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

// Kinetic landing headline: "Find your next " + a word that cycles through a
// small set, the rotating word emphasised in lime using the display face. ~1.8s per word, smooth fade + slide swap. Reduced-motion safe:
// when the user prefers reduced motion we render a single static word ("home")
// with no cycling, so the copy still reads cleanly.
const WORDS = ["loft", "condo", "studio", "home"];
const INTERVAL = 1800;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  !!window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

export default function RotatingHeadline({ className = "" }) {
  const [reduced] = useState(prefersReducedMotion);
  const [i, setI] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    if (reduced) return undefined;
    timer.current = setInterval(() => setI((v) => (v + 1) % WORDS.length), INTERVAL);
    return () => clearInterval(timer.current);
  }, [reduced]);

  // Reduced motion: static "Find your next home." — no animation, no cycling.
  if (reduced) {
    return (
      <h1 className={className}>
        Find your next <span className="font-display text-brand-primary">home</span>.
      </h1>
    );
  }

  const word = WORDS[i];
  return (
    <h1 className={className} aria-label="Find your next home">
      <span aria-hidden="true">Find your next </span>
      {/* Fixed-baseline slot so the swap never reflows the line. The widest
          word ("studio") reserves the width; each word slides + fades in. */}
      <span
        aria-hidden="true"
        className="relative inline-grid overflow-hidden align-baseline"
        style={{ minWidth: "3.6ch" }}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={word}
            data-testid="rotating-word"
            className="col-start-1 row-start-1 whitespace-nowrap font-display text-brand-primary"
            initial={{ y: "0.5em", opacity: 0 }}
            animate={{ y: "0em", opacity: 1 }}
            exit={{ y: "-0.5em", opacity: 0 }}
            transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
          >
            {word}
          </motion.span>
        </AnimatePresence>
      </span>
      <span aria-hidden="true">.</span>
    </h1>
  );
}
